import { BlockStruct } from '@/models/Blocks/block-struct';
import { BlockSession } from '@/models/Blocks/block-session';
import { TemplateStruct } from "@/models/Blocks/Itens/Types/template-struct";
import { BlockTesting } from '@/models/Blocks/block-testing';

interface pointsInfoStruct{
    text: string;
    total: number;
}
export class Block{
    protected _id: string;
    protected _key: string;
    protected _keyCache: string;
    protected _order: number;
    protected _borderColor?: string;
    protected _title: string;
    protected _titleAlign?: string;
    protected _oti?: string;
    protected _ttl: number = 0;
    protected _template: {checksum: string, struct: any[][][], properties?: {[key: string]: object}, height?: number, id?: string};
    protected _label: boolean = false;
    protected _params?: string;
    protected _specs?: string;
    protected _context?: string;
    protected _loading: boolean = false;
    protected _retryAttempt: boolean = false;
    protected _isVisible: boolean = true;
    protected _pointsInfo?: pointsInfoStruct = undefined;

    protected _content?: Array<object>|{[key: string]: object};
    protected _saveOnSession: boolean = true;
    protected _struct?: TemplateStruct;

    public constructor(id: string, order: number, title: string, template: {checksum: string, struct: any[][][], properties?: {[key: string]: object}, height?: number, id?: string}, params?: string, loadCache: boolean = false){
        this._id    = id;
        this._key   = `blk${id}`;
        this._order = order;
        this._title = title;
        this.params = params;
        this._template = template;

        const bs    = new BlockStruct(this._template.struct,[]);
        bs.key      = this._key;
        bs.skeleton = true;

        this._struct = bs.getStruct(template.properties);
        this._keyCache = `blk${id}_${this._struct.cols}`;
        if(template.height){
            this._struct.height = template.height;
        }
        
        const blockData = this.params && loadCache ? BlockSession.getInstance().getBlockData(this._keyCache,this.params,template.id??'') : null;
        if(blockData && blockData.content){
            this._title = blockData.title ? blockData.title : this._title;
            this._oti = blockData.oti ? blockData.oti : this._oti;
            this._processContent(blockData.content);
        }
    }

    /******************** GET *********************/
    public get id(): string{
        return this._id;
    }

    public get order(): number{
        return this._order;
    }

    public get title(): string{
        return this._title;
    }

    public get oti(): string|undefined{
        return this._oti;
    }

    public get ttl(): number{
        return this._ttl;
    }

    public get params(): string|undefined{
        return this._params;
    }

    public get specs(): string|undefined{
        return this._specs;
    }

    public get label(): boolean{
        return this._label;
    }

    public get struct(): TemplateStruct|undefined{
        return this._struct;
    }

    public get loading(): boolean{
        return this._loading;
    }

    public get isVisible(): boolean{
        return this._isVisible;
    }

    public get retayAttempt(): boolean{
        return this._retryAttempt;
    }

    public get properties(): {[key: string]: object}|undefined{
        return this._template.properties;
    }

    public get template():  {checksum: string, struct: any[][][], properties?: {[key: string]: object}, height?: number, id?: string}{
        return this._template;
    }

    public get borderColor():string|undefined{
        return this._borderColor;
    }

    public get titleAlign():string|undefined{
        return this._titleAlign;
    }

    public get context():string|undefined{
        return this._context;
    }

    public get saveOnSession(){
        return this._saveOnSession;
    }

    public get pointsInfo(){
        return this._pointsInfo;
    }

    /******************** SET *********************/
    public set oti(value: string|undefined){
        this._oti = value;
    }

    public set saveOnSession(value: boolean){
        this._saveOnSession = value;
    }

    public set ttl(value: number){
        this._ttl = value;
    }

    public set params(value: string|undefined){
        this._params = value;
    }

    public set specs(value: string|undefined){
        if(value){
            (new BlockTesting(value));
            try{
                const json = atob(value);
                const data = JSON.parse(json);
                this._context = data?.context ?? undefined;
            }catch (error) {}
        }
        this._specs = value;
    }

    public set label(value: boolean){
        this._label = value;
    }

    public set title(value: string){
        this._title = value;
    }

    public set borderColor(value: string){
        this._borderColor = value;
    }

    public set titleAlign(value: string|undefined){
        this._titleAlign = value;
    }

    public set content(value: Array<object>|{[key: string]: object}){
        if(this._keyCache && this._ttl && this._ttl > 0 && this.params){
            const blockData = {
                content: value,
                title: this._title,
                oti: this._oti,
            }
            if(this.saveOnSession){
                BlockSession.getInstance().setBlockData(blockData, this._keyCache, this.params, this.ttl, (this._template.id??''));
            }
        }
        this.retayAttempt = false;
        this._processContent(value);
    }

    public set loading(value: boolean){
        this._loading = value;
    }

    public set isVisible(value: boolean){
        this._isVisible = value;
    }

    public set retayAttempt(value: boolean){
        this._retryAttempt = value;
    }

    public set pointsInfo(value: pointsInfoStruct|undefined){
        this._pointsInfo = value;
    }

    /******************** METHODS *********************/
    public hasContent(): boolean{
        return this._content !== undefined;
    }

    protected _processContent(value: Array<object>|{[key: string]: object}){
        this._content = value;
        if((Array.isArray(value) && value.length > 0) || (typeof value == 'object' && Object.keys(value).length > 0)){
            const bs = new BlockStruct(this._template.struct,this._content);
            bs.blockName = this.oti;
            bs.key = this._key;

            this._struct = bs.getStruct(this._template.properties);
            if(this._struct && this._template.height){
                this._struct.height = this._template.height;
            }
            bs.skeleton = false;
        }else{
            this._struct = undefined;
        }
    }
}